import { AlertTriangle, CheckCircle2, Info, Loader2, RefreshCw, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { CnutDataImportJobDiagnosisRuleType, CnutDataImportJobDiagnosisSeverity } from "~/common/types/graphql";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui";
import type {
  DataImportJobDiagnosisHistoryItem,
  DataImportJobDiagnosisResult,
  DiagnosisStatus,
} from "~/features/dataImportProjects/hooks/useDataImportJobDiagnosis";
import { cn } from "~/lib/utils";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type DiagnosisItem = DataImportJobDiagnosisResult | DataImportJobDiagnosisHistoryItem;
type DiagnosisRuleResult = DiagnosisItem["results"][number];

type DiagnosisDialogProps = {
  open: boolean;
  readOnly: boolean;
  onClose: () => void;
  onRerun: () => void;
  status: DiagnosisStatus;
  result: DataImportJobDiagnosisResult | null;
  latestDiagnosis: DataImportJobDiagnosisHistoryItem | null;
  isStale: boolean;
};

const DATA_SOURCE_RULE_TYPES: readonly CnutDataImportJobDiagnosisRuleType[] = [
  "not_ready_data_source_rows",
  "data_source_schema_mismatch",
  "unmapped_data_source_fields",
  "unmapped_evidence_fields",
  "invalid_select_value_range",
];

const COMPOSER_RULE_TYPES: readonly CnutDataImportJobDiagnosisRuleType[] = [
  "invalid_composition_schema",
  "invalid_evidence_field_reference",
  "invalid_composer_data_source",
  "invalid_relation_resolver",
];

const RECORD_UPDATE_RULE_TYPES: readonly CnutDataImportJobDiagnosisRuleType[] = ["invalid_record_update_override"];

type ResultsByTargetAndRule = Map<string, Map<CnutDataImportJobDiagnosisRuleType, CnutDataImportJobDiagnosisSeverity>>;

const severityClasses: Record<CnutDataImportJobDiagnosisSeverity, string> = {
  ok: "border-score-a bg-score-a-bg text-score-a",
  warning: "border-score-b bg-score-b-bg text-[#B25612]",
  error: "border-score-c bg-score-c-bg text-score-c",
  info: "border-primary-200 bg-primary-50 text-primary-700",
};

const severityIcons: Record<CnutDataImportJobDiagnosisSeverity, ReactNode> = {
  ok: <CheckCircle2 className="size-3.5" />,
  warning: <AlertTriangle className="size-3.5" />,
  error: <XCircle className="size-3.5" />,
  info: <Info className="size-3.5" />,
};

function severityToLabel(severity: CnutDataImportJobDiagnosisSeverity): string {
  switch (severity) {
    case "ok":
      return "OK";
    case "warning":
      return "WARN";
    case "error":
      return "ERROR";
    default:
      return severity.toUpperCase();
  }
}

function SeverityBadge({ severity }: { severity: CnutDataImportJobDiagnosisSeverity }) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-[var(--radius-pill)] border px-2 py-0.5 text-[11px] font-semibold",
        severityClasses[severity],
      )}
    >
      {severityToLabel(severity)}
    </span>
  );
}

function InlineAlert({ tone, children }: { tone: "warning" | "error" | "info"; children: ReactNode }) {
  const icon =
    tone === "error" ? (
      <XCircle className="size-4" />
    ) : tone === "warning" ? (
      <AlertTriangle className="size-4" />
    ) : (
      <Info className="size-4" />
    );
  const className =
    tone === "error"
      ? "border-score-c bg-score-c-bg text-score-c"
      : tone === "warning"
        ? "border-score-b bg-score-b-bg text-[#B25612]"
        : "border-primary-200 bg-primary-50 text-primary-700";

  return (
    <div className={cn("flex items-start gap-2 rounded-[var(--radius-md)] border p-3 text-14-regular", className)}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function useRuleTypeLabels(): Record<CnutDataImportJobDiagnosisRuleType, string> {
  const { t } = useTranslation();
  return {
    data_source_schema_mismatch: t("feature.dataImportProjects.diagnosis.ruleType.schemaMismatch"),
    unmapped_data_source_fields: t("feature.dataImportProjects.diagnosis.ruleType.unmappedSourceFields"),
    unmapped_evidence_fields: t("feature.dataImportProjects.diagnosis.ruleType.unmappedEvidenceFields"),
    invalid_composition_schema: t("feature.dataImportProjects.diagnosis.ruleType.compositionSchema"),
    invalid_evidence_field_reference: t("feature.dataImportProjects.diagnosis.ruleType.evidenceFieldRef"),
    invalid_composer_data_source: t("feature.dataImportProjects.diagnosis.ruleType.dataSourceRef"),
    invalid_relation_resolver: t("feature.dataImportProjects.diagnosis.ruleType.relationResolver"),
    invalid_select_value_range: t("feature.dataImportProjects.diagnosis.ruleType.selectValueRange"),
    not_ready_data_source_rows: t("feature.dataImportProjects.diagnosis.ruleType.notReadyRows"),
    invalid_record_update_override: t("feature.dataImportProjects.diagnosis.ruleType.recordUpdateOverride"),
  };
}

function groupResultsByCategory(results: DiagnosisItem["results"]): {
  dataSourceTargets: ResultsByTargetAndRule;
  composerTargets: ResultsByTargetAndRule;
  recordUpdateTargets: ResultsByTargetAndRule;
} {
  const dataSourceTargets: ResultsByTargetAndRule = new Map();
  const composerTargets: ResultsByTargetAndRule = new Map();
  const recordUpdateTargets: ResultsByTargetAndRule = new Map();

  for (const result of results) {
    const targetMap = DATA_SOURCE_RULE_TYPES.includes(result.ruleType)
      ? dataSourceTargets
      : RECORD_UPDATE_RULE_TYPES.includes(result.ruleType)
        ? recordUpdateTargets
        : composerTargets;
    const existing = targetMap.get(result.target) ?? new Map();
    existing.set(result.ruleType, result.severity);
    targetMap.set(result.target, existing);
  }

  return { dataSourceTargets, composerTargets, recordUpdateTargets };
}

function SummarySection({
  title,
  ruleTypes,
  ruleTypeLabels,
  targetResults,
}: {
  title: string;
  ruleTypes: readonly CnutDataImportJobDiagnosisRuleType[];
  ruleTypeLabels: Record<CnutDataImportJobDiagnosisRuleType, string>;
  targetResults: ResultsByTargetAndRule;
}) {
  const { t } = useTranslation();

  if (targetResults.size === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <h4 className="text-14-bold text-fg-1">{title}</h4>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full min-w-[640px] border-collapse text-12-regular">
          <thead className="bg-bg-subtle text-fg-2">
            <tr>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("feature.dataImportProjects.diagnosis.target")}
              </th>
              {ruleTypes.map((ruleType) => (
                <th key={ruleType} className="border-b border-border-default px-3 py-2 text-center font-semibold">
                  {ruleTypeLabels[ruleType]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...targetResults.keys()].sort().map((target) => {
              const rulesMap = targetResults.get(target)!;
              return (
                <tr key={target} className="border-t border-border-default first:border-t-0">
                  <td className="px-3 py-2 text-fg-1">{target}</td>
                  {ruleTypes.map((ruleType) => {
                    const severity = rulesMap.get(ruleType);
                    return (
                      <td key={ruleType} className="px-3 py-2 text-center">
                        {severity ? <SeverityBadge severity={severity} /> : <span className="text-fg-3">-</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DiagnosisSummaryTable({ results }: { results: DiagnosisItem["results"] }) {
  const { t } = useTranslation();
  const ruleTypeLabels = useRuleTypeLabels();

  if (results.length === 0) {
    return null;
  }

  const { dataSourceTargets, composerTargets, recordUpdateTargets } = groupResultsByCategory(results);

  return (
    <div className="space-y-4">
      <SummarySection
        title={t("feature.dataImportProjects.diagnosis.dataSources")}
        ruleTypes={DATA_SOURCE_RULE_TYPES}
        ruleTypeLabels={ruleTypeLabels}
        targetResults={dataSourceTargets}
      />
      <SummarySection
        title={t("feature.dataImportProjects.diagnosis.composers")}
        ruleTypes={COMPOSER_RULE_TYPES}
        ruleTypeLabels={ruleTypeLabels}
        targetResults={composerTargets}
      />
      <SummarySection
        title={t("feature.dataImportProjects.diagnosis.recordUpdate")}
        ruleTypes={RECORD_UPDATE_RULE_TYPES}
        ruleTypeLabels={ruleTypeLabels}
        targetResults={recordUpdateTargets}
      />
    </div>
  );
}

function DiagnosisRuleCard({ result, hideTarget }: { result: DiagnosisRuleResult; hideTarget?: boolean }) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "rounded-[var(--radius-md)] border p-3",
        severityClasses[result.severity] ?? "border-border-default bg-bg-surface text-fg-1",
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">{severityIcons[result.severity]}</span>
        <div className="min-w-0 flex-1 space-y-2">
          {!hideTarget && <h5 className="text-14-bold">{result.target}</h5>}
          <p className="text-14-regular">{result.message}</p>
          {result.suggestion && (
            <p className="text-12-regular italic">
              {t("ui.suggestion")}: {result.suggestion}
            </p>
          )}
          <span className="inline-flex rounded-[var(--radius-pill)] border border-current px-2 py-0.5 text-[11px] font-semibold">
            {result.ruleType}
          </span>
        </div>
      </div>
    </div>
  );
}

function DiagnosisResultsPanel({ diagnosis }: { diagnosis: DiagnosisItem }) {
  const { t } = useTranslation();

  const issueResultsByTarget = useMemo(() => {
    const issues = diagnosis.results.filter((r) => r.severity === "warning" || r.severity === "error");
    const grouped = new Map<string, typeof issues>();
    for (const result of issues) {
      const existing = grouped.get(result.target) ?? [];
      existing.push(result);
      grouped.set(result.target, existing);
    }
    return grouped;
  }, [diagnosis.results]);

  return (
    <div className="space-y-4">
      <dl className="grid gap-1 text-12-regular text-fg-2">
        <div className="flex gap-2">
          <dt className="font-semibold">{t("ui.requestedBy")}:</dt>
          <dd>{diagnosis.requestedBy}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold">{t("ui.requestedAt")}:</dt>
          <dd>{diagnosis.requestedAt}</dd>
        </div>
      </dl>

      {diagnosis.summary && (
        <section className="space-y-1 rounded-[var(--radius-md)] bg-bg-subtle p-3">
          <h4 className="text-14-bold text-fg-1">{t("ui.summary")}</h4>
          <p className="whitespace-pre-wrap text-14-regular text-fg-1">{diagnosis.summary}</p>
        </section>
      )}

      {diagnosis.errorMessage ? <InlineAlert tone="error">{diagnosis.errorMessage}</InlineAlert> : null}

      {diagnosis.results.length > 0 ? (
        <div className="border-t border-border-default pt-4">
          <DiagnosisSummaryTable results={diagnosis.results} />
        </div>
      ) : null}

      {issueResultsByTarget.size > 0 ? (
        <section className="space-y-3 border-t border-border-default pt-4">
          <h4 className="text-14-bold text-fg-1">{t("feature.dataImportProjects.diagnosis.details")}</h4>
          {[...issueResultsByTarget.entries()].map(([target, results]) => (
            <div key={target} className="space-y-2">
              <h5 className="text-14-bold text-fg-1">{target}</h5>
              <div className="space-y-2">
                {results.map((result, index) => (
                  <DiagnosisRuleCard key={`${result.ruleType}-${index}`} result={result} hideTarget />
                ))}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {diagnosis.results.length === 0 && diagnosis.status !== "failed" ? (
        <p className="text-14-regular text-fg-2">{t("feature.dataImportProjects.diagnosis.noIssues")}</p>
      ) : null}
    </div>
  );
}

export function DiagnosisDialog({
  open,
  readOnly,
  onClose,
  onRerun,
  status,
  result,
  latestDiagnosis,
  isStale,
}: DiagnosisDialogProps) {
  const { t } = useTranslation();
  const isLoading = status === "invoking" || status === "polling";
  const displayedResult = result ?? latestDiagnosis;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isLoading) {
          onClose();
        }
      }}
    >
      <DialogContent aria-describedby={undefined} className="flex max-h-[85vh] max-w-4xl flex-col overflow-hidden">
        <DialogHeader className="pr-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle>{t("ui.configurationDiagnosis")}</DialogTitle>
            <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("feature.dataImportProjects.diagnosis.rerun")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!readOnly) onRerun();
                }}
                disabled={isLoading || readOnly}
              >
                <RefreshCw className={cn("size-3.5", isLoading && "animate-spin")} />
                {t("feature.dataImportProjects.diagnosis.rerun")}
              </Button>
            </DipReadOnlyTooltip>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex flex-col items-center py-8 text-fg-2">
                <Loader2 className="size-8 animate-spin" />
                <p className="mt-3 text-14-regular">{t("feature.dataImportProjects.diagnosis.running")}</p>
              </div>
            ) : null}

            {isLoading && latestDiagnosis ? (
              <section className="space-y-3 border-t border-border-default pt-4">
                <h3 className="text-14-bold text-fg-2">
                  {t("feature.dataImportProjects.diagnosis.label")} ({latestDiagnosis.requestedAt})
                </h3>
                <DiagnosisResultsPanel diagnosis={latestDiagnosis} />
              </section>
            ) : null}

            {!isLoading && displayedResult ? (
              <>
                {isStale ? (
                  <InlineAlert tone="warning">{t("feature.dataImportProjects.diagnosis.staleWarning")}</InlineAlert>
                ) : null}
                <DiagnosisResultsPanel diagnosis={displayedResult} />
              </>
            ) : null}

            {!isLoading && !displayedResult ? (
              <p className="py-8 text-center text-14-regular text-fg-2">
                {t("feature.dataImportProjects.diagnosis.noDiagnosisYet")}
              </p>
            ) : null}

            {!isLoading && status === "failed" && !result ? (
              <InlineAlert tone="error">{t("feature.dataImportProjects.diagnosis.errorOccurred")}</InlineAlert>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
